/**
 * `/issue`'s one popup (unit 26C; `frontend_shell.md` Q4 (c), Q5). It shows
 * everything a report carries — the person's words, the widget, and their
 * conversation since they last cleared it — and who will read each. Submit
 * sends exactly what it showed (`through_id`); after that the report is the
 * owner's, and the person cannot open it again.
 *
 * Since v2.03 (`issue_reports.md` rule 6) it also shows the text GitHub gets,
 * as the server builds it (`/api/issues/preview`), folded with its size (Q3),
 * and, for a member, what the owner reads besides. The action log is taken
 * once, as the popup opens, so Submit sends the log the preview showed.
 *
 * It needs signal: the thread is on the server, and is read from there so the
 * popup shows what will be sent, not what this screen happens to hold.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../api/client";
import { COMMIT, trail } from "../app/trail";
import { ok, reason } from "../api/query";
import { useMe } from "../app/me";
import { ActionButton } from "../ui/ActionButton";
import { Rich } from "../ui/Rich";
import styles from "./IssuePopup.module.css";
import { useIssue, whoReads, type Draft } from "./issue";
import { useChat } from "./store";

export function IssuePopup() {
  const draft = useIssue((s) => s.draft);
  return draft ? <Popup draft={draft} /> : null;
}

function Popup({ draft }: { draft: Draft }) {
  const me = useMe();
  const close = useIssue((s) => s.close);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Never cached: it is read fresh each time the popup opens.
  const thread = useQuery({
    queryKey: ["issues", "thread", draft],
    queryFn: () => ok(api.GET("/api/issues/thread")),
    gcTime: 0,
  });
  const messages = thread.data?.messages ?? [];
  const [log] = useState(trail);
  const sent = {
    report: draft.report,
    widget: draft.widget,
    tab: draft.tab,
    client: "web" as const,
    version: COMMIT,
    trail: log,
    through_id: messages.at(-1)?.id ?? null,
  };
  const preview = useQuery({
    queryKey: ["issues", "preview", draft, sent.through_id],
    queryFn: () => ok(api.POST("/api/issues/preview", { body: sent })),
    enabled: thread.isSuccess,
    gcTime: 0,
  });
  const github = preview.data ? `${preview.data.title}\n\n${preview.data.body}` : "";

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && !sending && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close, sending]);

  const submit = async () => {
    setSending(true);
    setError(null);
    try {
      await ok(api.POST("/api/issues", { body: sent }));
      close();
      useChat
        .getState()
        .say(me.owner ? "Report sent. It is in **Executive**, under Issues." : "Report sent.");
    } catch (e) {
      setError(`Not sent: ${reason(e)}`);
      setSending(false);
    }
  };

  return (
    <div className={styles.backdrop}>
      <div className={styles.popup} role="dialog" aria-modal="true" aria-labelledby="issue-head">
        <h2 id="issue-head" className={styles.head}>
          Report an issue
        </h2>
        <p className={styles.who}>{whoReads(me.owner)}</p>
        <section className={styles.part}>
          <h3 className={styles.label}>Your report{draft.widget && ` · from ${draft.widget}`}</h3>
          <p className={styles.report}>{draft.report}</p>
        </section>
        <section className={`${styles.part} ${styles.grow}`}>
          <h3 className={styles.label}>
            The conversation since you last cleared it
            {thread.isSuccess && ` · ${messages.length} message${messages.length === 1 ? "" : "s"}`}
          </h3>
          <div className={styles.thread}>
            {thread.isPending && <p className={styles.quiet}>Reading the conversation…</p>}
            {thread.isError && <p className={styles.error}>{reason(thread.error)}</p>}
            {thread.isSuccess && !messages.length && (
              <p className={styles.quiet}>None. Only your report is sent.</p>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`${styles.message} ${styles[m.role]}`}>
                <Rich source={m.content} />
              </div>
            ))}
          </div>
        </section>
        <section className={`${styles.part} ${styles.keep}`}>
          {preview.isPending && <p className={styles.quiet}>Building the report…</p>}
          {preview.isError && <p className={styles.error}>{reason(preview.error)}</p>}
          {preview.data && (
            <details className={styles.fold}>
              <summary className={styles.label}>
                What GitHub gets · {github.length.toLocaleString()} characters
              </summary>
              <pre className={styles.text}>{github}</pre>
            </details>
          )}
          {preview.data?.owner_reads && (
            <details className={styles.fold}>
              <summary className={styles.label}>What the owner also reads</summary>
              <pre className={styles.text}>{preview.data.owner_reads}</pre>
            </details>
          )}
        </section>
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.actions}>
          <ActionButton onClick={close} disabled={sending}>
            Cancel
          </ActionButton>
          <ActionButton
            primary
            onClick={() => void submit()}
            disabled={!preview.isSuccess || sending}
          >
            {sending ? "Sending…" : "Submit"}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}
